import type { FC, FormEvent, ReactNode } from 'react'
import type { GradeRecord, GradeType, Student } from '@/types'

import { useMemo, useState } from 'react'
import { cn } from '@/lib/utils'
import { DEMO_TODAY, formatDate } from '@/lib/dates'
import { average, formatDecimal } from '@/lib/academic'
import { downloadXlsx, readSpreadsheetFile } from '@/lib/spreadsheet'
import { Seo } from '@/components/generals/seo'
import { Badge } from '@/components/ui/badge'
import { Button, ButtonLink } from '@/components/ui/button'
import { Card, CardHeader } from '@/components/ui/card'
import { Avatar, EmptyState } from '@/components/ui/feedback'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { HistoryPicker } from '@/components/app/history-picker'
import { PageHeader } from '@/components/app/page-header'
import { PageSkeleton } from '@/components/app/page-skeleton'
import { SearchField } from '@/components/app/search-field'
import { useAcademic } from '@/components/providers/academic-provider'
import { useConfirm } from '@/components/providers/confirm-provider'
import { useToast } from '@/components/providers/toast-provider'
import { useQueryParams } from '@/hooks/use-query-params'
import { DEFAULT_KKM, GRADE_TYPE_LABELS, GRADE_TYPES } from '@/constants/academic'

import {
    THead,
    TBody,
    TD,
    TH,
    TR,
    Table
} from '@/components/ui/table'

import {
    ArrowRight,
    Check,
    CircleAlert,
    Download,
    FileDown,
    FileSpreadsheet,
    Target,
    Trash2,
    Upload,
    UserPlus
} from 'lucide-react'

const slug = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')

const parseScore = (value: string): number | null => {
    if (value.trim() === '') return null

    const parsed = Number(value.replace(',', '.'))

    return Number.isFinite(parsed) ? parsed : null
}

type GradeSheetProps = {
    classId: string
    subjectId: string
    type: GradeType
    date: string
    students: Student[]
    saved: Map<string, GradeRecord>
    savedKkm: number | null
    defaultKkm: number
    onReload: () => void
}

const GradeSheet: FC<GradeSheetProps> = ({
    classId,
    subjectId,
    type,
    date,
    students,
    saved,
    savedKkm,
    defaultKkm,
    onReload
}): ReactNode => {
    const { actions } = useAcademic()
    const confirm = useConfirm()
    const toast = useToast()

    const [scores, setScores] = useState<Record<string, string>>(() => {
        return Object.fromEntries(students.map(student => {
            const score = saved.get(student.id)?.score

            return [student.id, score === undefined ? '' : String(score)]
        }))
    })

    const [kkmInput, setKkmInput] = useState(savedKkm === null ? '' : String(savedKkm))
    const [query, setQuery] = useState('')

    const threshold = parseScore(kkmInput) ?? defaultKkm

    const stats = useMemo(() => {
        const values = students
            .map(student => parseScore(scores[student.id] ?? ''))
            .filter((value): value is number => value !== null)

        const passed = values.filter(value => value >= threshold).length

        return {
            filled: values.length,
            average: average(values),
            passed,
            failed: values.length - passed
        }
    }, [scores, students, threshold])

    const search = query.trim().toLowerCase()
    const visible = students.filter(student => `${student.name} ${student.nis}`.toLowerCase().includes(search))

    const handleSave = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const kkm = kkmInput.trim() === '' ? null : parseScore(kkmInput)

        if (kkmInput.trim() !== '' && kkm === null) {
            toast.error('Gagal menyimpan', 'Nilai KKM harus berupa angka 0 sampai 100.')

            return
        }

        const result = actions.saveGrades({
            classId,
            subjectId,
            type,
            date,
            kkm,
            scores: Object.fromEntries(students.map(student => [student.id, parseScore(scores[student.id] ?? '')]))
        })

        if (result.ok) {
            toast.success(result.message ?? 'Nilai berhasil disimpan.')
            onReload()
        }
        else {
            toast.error('Gagal menyimpan', result.error)
        }
    }

    const handleDeleteRow = async (student: Student, record: GradeRecord): Promise<void> => {
        const approved = await confirm({
            title: 'Hapus catatan nilai siswa ini?',
            description: `Nilai ${student.name} pada ${formatDate(date, 'short')} akan dihapus.`,
            confirmLabel: 'Ya, hapus'
        })

        if (!approved) return

        actions.deleteGrade(record.id)
        toast.success('Catatan nilai siswa dihapus.')
        onReload()
    }

    const statTiles = [
        { label: 'Jumlah siswa', value: String(students.length), hint: 'Siswa di kelas terpilih', tone: 'text-foreground' },
        { label: 'Nilai terisi', value: String(stats.filled), hint: 'Kolom sudah memiliki nilai', tone: 'text-foreground' },
        { label: 'Rata-rata kelas', value: stats.average === null ? '—' : formatDecimal(stats.average, 1), hint: 'Rata-rata nilai terisi', tone: 'text-foreground' },
        { label: 'Tuntas (≥ KKM)', value: stats.filled ? String(stats.passed) : '—', hint: `Acuan KKM: ${formatDecimal(threshold, 1)}`, tone: 'text-success' },
        { label: 'Perlu remedial', value: stats.filled ? String(stats.failed) : '—', hint: 'Di bawah batas KKM', tone: 'text-destructive' }
    ]

    return (
        <form onSubmit={handleSave}>
            <div className='mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5'>
                {statTiles.map(tile => (
                    <div key={tile.label} className='rounded-xl border bg-card p-4 shadow-card last:col-span-2 md:last:col-span-1'>
                        <p className='text-xs font-medium text-muted-foreground'>{tile.label}</p>
                        <p className={cn('mt-1 text-2xl leading-tight font-bold', tile.tone)}>{tile.value}</p>
                        <p className='mt-0.5 truncate text-xs text-muted-foreground'>{tile.hint}</p>
                    </div>
                ))}
            </div>

            <Card className='mb-6 flex flex-col gap-5 p-5 lg:flex-row lg:items-center lg:justify-between'>
                <div className='flex min-w-0 items-start gap-4'>
                    <span className='grid size-11 shrink-0 place-items-center rounded-xl bg-warning/12 text-warning' aria-hidden='true'>
                        <Target className='size-5' />
                    </span>

                    <div className='min-w-0'>
                        <Badge tone='warning'>KKM / Ambang Batas Remedial</Badge>

                        <h2 className='mt-2 text-[15px] font-semibold text-foreground'>
                            Batas Ketuntasan Nilai Siswa
                        </h2>

                        <p className='mt-0.5 max-w-xl text-[13px] leading-relaxed text-muted-foreground'>
                            Nilai di bawah batas KKM otomatis ditandai remedial di lembar input &amp; laporan harian. (Opsional / tidak wajib diisi).
                        </p>
                    </div>
                </div>

                <div className='shrink-0'>
                    <label htmlFor='kkmInput' className='mb-1.5 block text-[13px] font-semibold text-foreground'>
                        Nilai KKM:
                    </label>

                    <div className='relative w-40'>
                        <Input
                            id='kkmInput'
                            type='number'
                            step='0.1'
                            min='0'
                            max='100'
                            value={kkmInput}
                            onChange={event => setKkmInput(event.target.value)}
                            placeholder={formatDecimal(defaultKkm, 1)}
                            className='pr-14 text-center text-base font-semibold'
                        />

                        <span className='pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-[13px] text-muted-foreground'>
                            / 100
                        </span>
                    </div>

                    <p className='mt-1.5 text-xs text-muted-foreground'>
                        (Standar mapel: {formatDecimal(defaultKkm, 1)})
                    </p>
                </div>
            </Card>

            <Card>
                <CardHeader
                    eyebrow={`${GRADE_TYPE_LABELS[type]} · ${formatDate(date, 'short')}`}
                    title='Nilai siswa pada tanggal ini'
                    action={(
                        <SearchField
                            label='Cari siswa'
                            placeholder='Cari nama atau NIS...'
                            value={query}
                            onChange={event => setQuery(event.target.value)}
                            wrapperClassName='w-full sm:w-64'
                        />
                    )}
                />

                <Table>
                    <THead>
                        <tr>
                            <TH>Siswa</TH>
                            <TH className='w-44'>Nilai (0–100)</TH>
                            <TH className='min-w-56'>Indikator &amp; Status</TH>
                            <TH className='w-32 text-center'>Aksi</TH>
                        </tr>
                    </THead>

                    <TBody>
                        {visible.map(student => {
                            const raw = scores[student.id] ?? ''
                            const score = parseScore(raw)
                            const record = saved.get(student.id)
                            const passed = score !== null && score >= threshold

                            return (
                                <TR key={student.id}>
                                    <TD>
                                        <span className='flex items-center gap-3'>
                                            <Avatar name={student.name} />

                                            <span className='min-w-0'>
                                                <span className='block font-semibold text-foreground'>{student.name}</span>
                                                <span className='block text-xs text-muted-foreground'>NIS {student.nis}</span>
                                            </span>
                                        </span>
                                    </TD>

                                    <TD>
                                        <div className='relative'>
                                            <Input
                                                type='number'
                                                step='0.01'
                                                min='0'
                                                max='100'
                                                inputMode='decimal'
                                                value={raw}
                                                onChange={event => setScores(current => ({ ...current, [student.id]: event.target.value }))}
                                                aria-label={`Nilai ${student.name}`}
                                                aria-invalid={score !== null && (score < 0 || score > 100) ? true : undefined}
                                                className='h-9 pr-12 font-semibold tabular-nums'
                                            />

                                            <span className='pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-xs text-muted-foreground'>
                                                / 100
                                            </span>
                                        </div>
                                    </TD>

                                    <TD>
                                        <div className='h-1.5 w-full overflow-hidden rounded-full bg-muted' aria-hidden='true'>
                                            <div
                                                className={cn('h-full rounded-full transition-[width] duration-300', score === null ? 'bg-transparent' : passed ? 'bg-success' : 'bg-destructive')}
                                                style={{ width: `${score === null ? 0 : Math.max(0, Math.min(100, score))}%` }}
                                            />
                                        </div>

                                        <div className='mt-2 flex items-center justify-between gap-2'>
                                            <span className='text-xs text-muted-foreground tabular-nums'>
                                                {score === null ? '' : passed ? `+${formatDecimal(score - threshold, 1)} dari KKM` : `${formatDecimal(threshold - score, 1)} di bawah KKM`}
                                            </span>

                                            {score === null ? (
                                                <Badge tone='neutral'>Belum dinilai</Badge>
                                            ) : passed ? (
                                                <Badge tone='success' dot>Tuntas</Badge>
                                            ) : (
                                                <Badge tone='danger' dot>Remedial</Badge>
                                            )}
                                        </div>
                                    </TD>

                                    <TD className='text-center'>
                                        {record ? (
                                            <Button variant='danger-outline' size='xs' onClick={() => void handleDeleteRow(student, record)}>
                                                Hapus nilai
                                            </Button>
                                        ) : (
                                            <span className='text-muted-foreground'>—</span>
                                        )}
                                    </TD>
                                </TR>
                            )
                        })}

                        {visible.length === 0 && (
                            <tr>
                                <td colSpan={4} className='px-5 py-10 text-center text-sm text-muted-foreground'>
                                    Tidak ada siswa yang cocok dengan pencarian.
                                </td>
                            </tr>
                        )}
                    </TBody>
                </Table>

                <div className='flex flex-col gap-3 border-t px-5 py-4 sm:flex-row sm:items-center sm:justify-between'>
                    <p className='max-w-xl text-xs leading-relaxed text-muted-foreground'>
                        Nilai disimpan per siswa dan tanggal. Mengisi ulang tanggal yang sama akan memperbarui nilai &amp; KKM.
                    </p>

                    <Button type='submit' className='sm:px-6'>
                        <Check aria-hidden='true' />
                        Simpan nilai harian
                    </Button>
                </div>
            </Card>
        </form>
    )
}

const GradesPage: FC = (): ReactNode => {
    const { classes, subjects, students, grades, classById, subjectById, actions } = useAcademic()
    const { ready, get, set } = useQueryParams()
    const confirm = useConfirm()
    const toast = useToast()

    const [version, setVersion] = useState(0)
    const [file, setFile] = useState<File | null>(null)
    const [fileKey, setFileKey] = useState(0)
    const [importing, setImporting] = useState(false)

    if (!ready) return <PageSkeleton />

    const requestedClass = get('kelas')
    const classId = classes.some(item => item.id === requestedClass) ? requestedClass as string : classes[0]?.id ?? ''
    const requestedSubject = get('mapel')
    const subjectId = subjects.some(item => item.id === requestedSubject) ? requestedSubject as string : subjects[0]?.id ?? ''
    const requestedType = get('jenis') as GradeType | undefined
    const type: GradeType = requestedType && GRADE_TYPES.includes(requestedType) ? requestedType : 'harian'
    const requestedDate = get('tanggal') ?? ''
    const date = /^\d{4}-\d{2}-\d{2}$/.test(requestedDate) ? requestedDate : DEMO_TODAY

    const subject = subjectById.get(subjectId)
    const defaultKkm = subject?.kkm ?? DEFAULT_KKM
    const classStudents = students.filter(student => student.classId === classId)
    const classStudentIds = new Set(classStudents.map(student => student.id))
    const scoped = grades.filter(record => record.subjectId === subjectId && record.type === type && record.date === date)
    const saved = new Map(scoped.filter(record => classStudentIds.has(record.studentId)).map(record => [record.studentId, record]))
    const savedKkm = scoped.find(record => record.kkm !== null)?.kkm ?? null

    const historyDates = Array.from(new Set(
        grades
            .filter(record => classStudentIds.has(record.studentId) && record.subjectId === subjectId && record.type === type)
            .map(record => record.date)
    )).sort().reverse().slice(0, 30)

    const hasSetup = classes.length > 0 && subjects.length > 0
    const reload = (): void => setVersion(value => value + 1)

    const handleTemplate = async (): Promise<void> => {
        try {
            await downloadXlsx('template-import-nilai.xlsx', [
                ['NIS', 'Nama Siswa', 'Nilai'].map(value => ({ value, header: true })),
                ...Array.from({ length: 36 }, () => [{ value: '' }, { value: '' }, { value: '' }])
            ], { sheet: 'Nilai', columns: [16, 34, 10], headerRows: 1 })
        }
        catch {
            toast.error('Template gagal dibuat', 'Coba unduh sekali lagi.')
        }
    }

    const handleExport = async (): Promise<void> => {
        const className = classById.get(classId)?.name ?? 'kelas'

        try {
            await downloadXlsx(`nilai-${slug(className)}-${slug(subject?.name ?? 'mapel')}-${type}-${date}.xlsx`, [
                ['NIS', 'Nama Siswa', 'Nilai'].map(value => ({ value, header: true })),
                ...classStudents.map(student => [{ value: student.nis }, { value: student.name }, { value: saved.get(student.id)?.score ?? '' }])
            ], { sheet: 'Nilai', columns: [16, 34, 10], headerRows: 1 })
        }
        catch {
            toast.error('Ekspor gagal', 'Coba unduh sekali lagi.')
        }
    }

    const handleImport = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault()

        if (!file) {
            toast.error('Pilih file nilai terlebih dahulu', 'Gunakan file CSV, XML Spreadsheet, atau Excel (.xlsx).')

            return
        }

        setImporting(true)

        try {
            const sheet = await readSpreadsheetFile(file)
            const headerIndex = sheet.findIndex(row => {
                const labels = row.map(cell => cell.toLowerCase().trim())

                return labels.includes('nis') && labels.includes('nilai')
            })

            const header = headerIndex >= 0 ? sheet[headerIndex].map(cell => cell.toLowerCase().trim()) : []
            const nisColumn = headerIndex >= 0 ? header.indexOf('nis') : 0
            const scoreColumn = headerIndex >= 0 ? header.indexOf('nilai') : 2

            const rows = sheet
                .map((row, index) => ({ line: index + 1, nis: (row[nisColumn] ?? '').trim(), score: (row[scoreColumn] ?? '').trim() }))
                .filter((row, index) => index !== headerIndex && row.nis !== '' && row.score !== '')

            const result = actions.importGrades({ classId, subjectId, type, date, rows })

            if (!result.ok) {
                toast.error('Impor ditolak', result.error)

                return
            }

            toast.success(result.message ?? 'Nilai berhasil diimpor.')
            setFile(null)
            setFileKey(key => key + 1)
            reload()
        }
        catch (error) {
            toast.error('Impor gagal', error instanceof Error ? error.message : 'File tidak dapat dibaca.')
        }
        finally {
            setImporting(false)
        }
    }

    const handleDeleteDate = async (): Promise<void> => {
        const approved = await confirm({
            title: `Hapus SEMUA nilai pada tanggal ${formatDate(date, 'short')} untuk kelas, mapel, dan jenis ini?`,
            description: 'Tindakan ini tidak dapat dibatalkan.',
            confirmLabel: 'Hapus semua'
        })

        if (!approved) return

        const result = actions.deleteGradeDate({ classId, subjectId, type, date })

        if (result.ok) toast.success(result.message ?? 'Nilai dihapus.')

        set({ tanggal: DEMO_TODAY })
        reload()
    }

    const reportHref = `/laporan-harian?view=nilai&kelas=${classId}&mapel=${subjectId}&jenis=${type}&tanggal=${date}`

    return (
        <>
            <Seo title='Penilaian' />

            <PageHeader
                eyebrow='Hasil belajar'
                title='Input penilaian'
                description='Catat dan tinjau nilai siswa berdasarkan kelas, mata pelajaran, jenis penilaian, dan batas KKM.'
                actions={(
                    <ButtonLink href={reportHref} variant='outline' size='sm'>
                        Lihat laporan harian
                        <ArrowRight aria-hidden='true' />
                    </ButtonLink>
                )}
            />

            <Card className='mb-6 space-y-4 p-4 sm:p-5'>
                <div className='grid gap-4 sm:grid-cols-2 xl:grid-cols-4'>
                    <Field label='Kelas' htmlFor='gradeClass'>
                        <Select id='gradeClass' value={classId} onChange={event => set({ kelas: event.target.value })}>
                            {classes.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </Field>

                    <Field label='Mata pelajaran' htmlFor='gradeSubject'>
                        <Select id='gradeSubject' value={subjectId} onChange={event => set({ mapel: event.target.value })}>
                            {subjects.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                        </Select>
                    </Field>

                    <Field label='Jenis penilaian' htmlFor='gradeType'>
                        <Select id='gradeType' value={type} onChange={event => set({ jenis: event.target.value })}>
                            {GRADE_TYPES.map(item => <option key={item} value={item}>{GRADE_TYPE_LABELS[item]}</option>)}
                        </Select>
                    </Field>

                    <Field label='Tanggal nilai' htmlFor='gradeDate'>
                        <Input
                            id='gradeDate'
                            type='date'
                            value={date}
                            onChange={event => {
                                if (event.target.value) set({ tanggal: event.target.value })
                            }}
                        />
                    </Field>
                </div>

                <HistoryPicker
                    id='gradeHistory'
                    title='Lihat penilaian sebelumnya'
                    description='Pilih tanggal untuk membuka nilai yang pernah dicatat.'
                    emptyText='Belum ada riwayat nilai untuk pilihan ini.'
                    dates={historyDates}
                    value={date}
                    onSelect={value => set({ tanggal: value })}
                />
            </Card>

            {hasSetup && (
                <Card className='mb-6'>
                    <CardHeader
                        icon={<FileSpreadsheet />}
                        title='Impor / ekspor nilai'
                        description='Template Excel berisi tabel kosong untuk diisi. Ekspor memuat nilai sesuai pilihan kelas, mapel, jenis, dan tanggal.'
                        action={(
                            <>
                                <Button variant='outline' size='sm' onClick={() => void handleTemplate()}>
                                    <Download aria-hidden='true' />
                                    Unduh template Excel
                                </Button>

                                <Button variant='secondary' size='sm' onClick={() => void handleExport()}>
                                    <FileDown aria-hidden='true' />
                                    Ekspor nilai Excel
                                </Button>
                            </>
                        )}
                    />

                    <form onSubmit={event => void handleImport(event)} className='flex flex-col gap-3 p-5 sm:flex-row sm:items-center'>
                        <Input
                            key={fileKey}
                            type='file'
                            accept='.csv,.xml,.xlsx,text/csv,text/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                            aria-label='Pilih file nilai'
                            onChange={event => setFile(event.target.files?.[0] ?? null)}
                            className='sm:flex-1'
                        />

                        <Button
                            type='submit'
                            variant='outline'
                            loading={importing}
                            className='border-success/40 text-success hover:border-success/60 hover:bg-success/10 hover:text-success'
                        >
                            <Upload aria-hidden='true' />
                            Import nilai
                        </Button>
                    </form>
                </Card>
            )}

            {!hasSetup ? (
                <Card>
                    <EmptyState
                        icon={<CircleAlert />}
                        title='Lengkapi data terlebih dahulu'
                        description='Penilaian perlu memiliki kelas, siswa di dalam kelas, dan mata pelajaran.'
                        action={(
                            <>
                                {classes.length === 0 && <ButtonLink href='/siswa' variant='outline' size='sm'>Tambah siswa dan kelas</ButtonLink>}
                                {subjects.length === 0 && <ButtonLink href='/mapel' size='sm'>Tambah mata pelajaran</ButtonLink>}
                            </>
                        )}
                    />
                </Card>
            ) : classStudents.length === 0 ? (
                <Card>
                    <EmptyState
                        icon={<UserPlus />}
                        title='Belum ada siswa di kelas ini'
                        description='Tambahkan siswa terlebih dahulu sebelum memasukkan nilai.'
                        action={<ButtonLink href={`/siswa?kelas=${classId}`} size='sm'>Kelola siswa</ButtonLink>}
                    />
                </Card>
            ) : (
                <>
                    <GradeSheet
                        key={`${classId}-${subjectId}-${type}-${date}-${version}`}
                        classId={classId}
                        subjectId={subjectId}
                        type={type}
                        date={date}
                        students={classStudents}
                        saved={saved}
                        savedKkm={savedKkm}
                        defaultKkm={defaultKkm}
                        onReload={reload}
                    />

                    {saved.size > 0 && (
                        <div className='mt-6 flex flex-col gap-3 rounded-xl border border-destructive/25 bg-destructive/5 p-4 sm:flex-row sm:items-center sm:justify-between'>
                            <div>
                                <p className='text-sm font-semibold text-foreground'>
                                    Hapus seluruh nilai tanggal ini?
                                </p>

                                <p className='text-[13px] text-muted-foreground'>
                                    Semua nilai kelas, mata pelajaran, dan jenis penilaian terpilih pada {formatDate(date, 'short')} akan dihapus.
                                </p>
                            </div>

                            <Button variant='danger-outline' onClick={() => void handleDeleteDate()}>
                                <Trash2 aria-hidden='true' />
                                Hapus semua nilai tanggal ini
                            </Button>
                        </div>
                    )}
                </>
            )}
        </>
    )
}

export default GradesPage
